import type { GradingProvider, ProviderGradeResult } from '../lib/types'
import { formatGrade, percentArticle, toPercent } from '../lib/format'
import { ProviderLogo } from './icons'
import { ProviderTabs } from './ProviderTabs'
import styles from './report.module.css'

const HIGH_CONFIDENCE = 0.7

export function GradeSummary({
  providers,
  provider,
  onSelectProvider,
  result,
  embedded = false,
}: {
  providers: GradingProvider[]
  provider: GradingProvider
  onSelectProvider: (p: GradingProvider) => void
  result: ProviderGradeResult
  embedded?: boolean
}) {
  const entries = Object.entries(result.confidence_map)
    .map(([grade, p]) => ({ grade: Number(grade), p }))
    .filter((s) => toPercent(s.p) > 0)

  if (!entries.length) return null

  const top = entries.reduce((a, b) => (b.p > a.p ? b : a), entries[0])
  // Show only the three strongest probabilities, ordered by grade for scanning.
  const segments = [...entries]
    .sort((a, b) => b.p - a.p || b.grade - a.grade)
    .slice(0, 3)
    .sort((a, b) => a.grade - b.grade)

  const confident = top.p >= HIGH_CONFIDENCE

  const content = (
    <>
      <ProviderTabs
        providers={providers}
        selected={provider}
        onSelect={onSelectProvider}
      />
      <h2 className={styles.summarySentence}>
        This card has {percentArticle(toPercent(top.p))}{' '}
        <span className={confident ? styles.summaryPct : styles.summaryPctWarn}>
          {toPercent(top.p)}%
        </span>{' '}
        chance of receiving a{' '}
        <span className={styles.summaryProvider}>
          <ProviderLogo provider={provider} height={17} />{' '}
          {formatGrade(top.grade, provider)}.
        </span>
      </h2>

      <div className={styles.distribution}>
        {segments.map((s) => {
          const isTop = s.grade === top.grade
          const topClass = confident ? styles.segmentTop : styles.segmentTopWarn
          return (
            <div
              key={s.grade}
              className={styles.segmentCol}
              style={{ flex: Math.max(s.p, 0.12) }}
            >
              <div className={`${styles.segment} ${isTop ? topClass : ''}`}>
                {toPercent(s.p)}%
              </div>
              <div className={styles.segmentLabel}>
                {formatGrade(s.grade, provider)}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )

  if (embedded) return content

  return (
    <section className={styles.card} aria-label="Grade prediction">
      {content}
    </section>
  )
}
